import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { authService } from '../../services/auth/authService';
import { patientsService, PatientMealPlan } from '../../services/patients/patientsService';
import { mealPlanService, MealPlanVersionSummary } from '../../services/nutrition/mealPlanService';
import DietPlanLayout from './DietPlanLayout';
import PatientMealPlanTab from './PatientMealPlanTab';
import { SECTION_LABELS } from '../../components/layout/DashboardSidebar/sidebarItems';

/** View one meal plan. Route: /patients/:patientId/diet-plan/:goalId/daily-meals/:planId */
const MealPlanViewPage: React.FC = () => {
  const navigate = useNavigate();
  const { patientId, goalId, planId } = useParams<{ patientId: string; goalId: string; planId: string }>();
  const [mealPlan, setMealPlan] = useState<PatientMealPlan | null>(null);
  const [patientName, setPatientName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [versions, setVersions] = useState<MealPlanVersionSummary[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [mealPlanName, setMealPlanName] = useState<string | null>(null);
  const [mealPlanStartDate, setMealPlanStartDate] = useState<string | null>(null);
  const [mealPlanEndDate, setMealPlanEndDate] = useState<string | null>(null);
  const lucidFontFamily = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

  const loadDietPlan = useCallback(async () => {
    if (!patientId || !goalId) {
      setError('Invalid patient or diet plan identifier');
      setLoading(false);
      return;
    }
    const nPatientId = Number(patientId);
    const nGoalId = Number(goalId);
    if (Number.isNaN(nPatientId) || Number.isNaN(nGoalId)) {
      setError('Invalid patient or diet plan identifier');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await patientsService.getPatientMealPlanByGoalId(nPatientId, nGoalId);
      if (!data) {
        setError('Diet plan not found');
        return;
      }
      setMealPlan(data);
      if (planId) {
        try {
          const [currentVersion, versionList, allPlans] = await Promise.all([
            mealPlanService.getCurrentVersion(planId),
            mealPlanService.listVersions(planId).catch(() => []),
            mealPlanService.listPlansForPatient(nPatientId).catch(() => []),
          ]);
          const sortedVersions = (versionList || []).slice().sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
          setVersions(sortedVersions);
          setSelectedVersionId(currentVersion.version_id);
          const matchedPlan = (allPlans || []).find((p) => p.plan_id === planId);
          setMealPlanName(matchedPlan?.name?.trim() || null);
          setMealPlanStartDate(matchedPlan?.start_date ?? null);
          setMealPlanEndDate(matchedPlan?.end_date ?? null);
        } catch {
          setVersions([]);
          setSelectedVersionId(null);
          setMealPlanName(null);
          setMealPlanStartDate(null);
          setMealPlanEndDate(null);
        }
      }
      try {
        const patients = await patientsService.getPatients();
        setPatientName(patients.find((p) => p.id === nPatientId)?.name ?? null);
      } catch {
        setPatientName(null);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load diet plan');
    } finally {
      setLoading(false);
    }
  }, [patientId, goalId, planId]);

  useEffect(() => {
    loadDietPlan();
  }, [loadDietPlan]);

  if (!authService.isAuthenticated()) return null;

  const formatDate = (value: string | null) => (value ? new Date(value).toLocaleDateString() : '—');
  const formatDuration = (start: string | null, end: string | null) => {
    if (!start || !end) return '—';
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return '—';
    const diffDays = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    if (diffDays <= 0) return '—';
    return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
  };

  const mealsBasePath = patientId && goalId ? `/patients/${patientId}/diet-plan/${goalId}/daily-meals` : undefined;
  const breadcrumbSegments = [
    { label: 'Meal Plan', to: mealsBasePath },
    { label: 'View Plan' },
  ];
  const pageTitle = (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 10 }}>
      <span>Meal Plan</span>
      {mealPlanName ? (
        <span style={{ fontSize: 'inherit', fontWeight: 'inherit', color: '#111827' }}>- {mealPlanName}</span>
      ) : null}
    </span>
  );
  const selectedVersion = selectedVersionId ? versions.find((v) => v.version_id === selectedVersionId) : null;
  const planStatusLabel = selectedVersion?.status
    ? `${selectedVersion.status.charAt(0).toUpperCase()}${selectedVersion.status.slice(1)}`
    : '—';

  const metaRight = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Selected Version
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div className="np-field" style={{ marginBottom: 0 }}>
          <select
            className="np-select--styled"
            value={selectedVersionId ?? ''}
            onChange={(e) => setSelectedVersionId(e.target.value || null)}
            style={{ minWidth: 230 }}
          >
            {versions.length > 0 ? (
              versions.map((v) => {
                const shortId = v.version_id.slice(-5);
                const status = v.status ? `${v.status.charAt(0).toUpperCase()}${v.status.slice(1)}` : 'Old';
                const dateStr = v.created_at ? new Date(v.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
                return (
                  <option key={v.version_id} value={v.version_id}>
                    Version {shortId} • {status} • {dateStr}
                  </option>
                );
              })
            ) : (
              <option value="">No versions</option>
            )}
          </select>
        </div>
        <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: lucidFontFamily }}>{selectedVersionId ? selectedVersionId.slice(0, 8) : ''}</span>
      </div>
    </div>
  );

  const titleRight = (
    <div
      role="button"
      tabIndex={0}
      onClick={() => mealsBasePath && navigate(mealsBasePath)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (mealsBasePath) navigate(mealsBasePath);
        }
      }}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#6b7280', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer' }}
    >
      <ChevronLeft size={14} strokeWidth={2} />
      <span>Back to list</span>
    </div>
  );

  return (
    <DietPlanLayout
      patientId={patientId!}
      goalId={goalId!}
      patientName={patientName}
      mealPlan={mealPlan}
      breadcrumbSegments={breadcrumbSegments}
      pageTitle={pageTitle}
      loading={loading}
      error={error}
      titleRight={titleRight}
      hideMetaCard
    >
      <div style={{ marginTop: 0, marginBottom: 16, backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '12px 14px', borderBottom: '1px solid #e5e7eb' }}>
          <span style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            Diet Plan
          </span>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 1fr) auto auto auto auto', alignItems: 'start', gap: 0 }}>
            <div style={{ paddingRight: 18 }}>
              <span style={{ fontSize: '1.02rem', fontWeight: 700, color: '#111827' }}>{mealPlan?.goalName ?? '—'}</span>
            </div>
            <div style={{ borderLeft: '1px solid #e5e7eb', padding: '0 18px' }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Effective Since
              </div>
              <div style={{ fontSize: '1rem', fontWeight: 600, color: '#111827', marginTop: 2 }}>
                {mealPlan?.effectiveAt ? new Date(mealPlan.effectiveAt).toLocaleDateString() : '—'}
              </div>
            </div>
            <div style={{ borderLeft: '1px solid #e5e7eb', padding: '0 18px' }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Meal Plan Duration
              </div>
              <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#111827', marginTop: 2, whiteSpace: 'nowrap' }}>
                {formatDate(mealPlanStartDate)} - {formatDate(mealPlanEndDate)}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>
                {formatDuration(mealPlanStartDate, mealPlanEndDate)}
              </div>
            </div>
            <div style={{ borderLeft: '1px solid #e5e7eb', padding: '0 18px' }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Plan Status
              </div>
              <div style={{ fontSize: '1rem', fontWeight: 600, color: '#111827', marginTop: 2 }}>
                {planStatusLabel}
              </div>
            </div>
            <div style={{ borderLeft: '1px solid #e5e7eb', paddingLeft: 18 }}>
              {metaRight}
            </div>
          </div>
        </div>
        <div style={{ padding: 12 }}>
        <PatientMealPlanTab
          patientId={Number(patientId)}
          goalId={goalId ? Number(goalId) : undefined}
          mealPlanViewMode="view"
          mealPlanIdFromUrl={planId}
          hideBackToList
          externalViewedVersionId={selectedVersionId ?? undefined}
          hideInlineVersionSelector
        />
        </div>
      </div>
    </DietPlanLayout>
  );
};

export default MealPlanViewPage;
